import { ValueProvider } from '@angular/core';
import { Predicate } from 'breeze-client';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AsyncValidator,
  parseOptionalChaining,
  SyncValidator,
  ValidationContextBase,
  VALIDATOR_FACTORY,
} from '.';
import { UnitOfWorkBase } from '../services';

export class SyncUniqueValidator extends SyncValidator {
  private static messageTemplate = '%displayName% must be unique';

  constructor(
    group: string,
    validatorContext?: Partial<ValidationContextBase>
  ) {
    super(
      'unique',
      group,
      (value, context) => {
        if (!value) {
          return true;
        }

        const unitOfWork = context.unitOfWork as UnitOfWorkBase;
        // context.model may not be the actual entity
        const entityType = unitOfWork.metadataStore.getAsEntityType(
          context.modelType,
          true
        );
        if (!entityType) {
          return true;
        }
        const model = unitOfWork.getEntity(
          entityType,
          ...entityType.keyProperties.map((p) => context.model[p.name])
        );
        return (
          unitOfWork
            .getEntities(context.modelType)
            .filter(
              (entity) =>
                entity !== model && entity[context.propertyName] === value
            ).length === 0
        );
      },
      {
        ...validatorContext,
        messageTemplate:
          validatorContext?.messageTemplate ||
          SyncUniqueValidator.messageTemplate,
      }
    );
  }

  static create(
    group: string,
    context?: Partial<ValidationContextBase>
  ): SyncUniqueValidator {
    return new SyncUniqueValidator(group, context);
  }
}

export class AsyncUniqueValidator extends AsyncValidator {
  private static messageTemplate = '%displayName% must be unique';

  constructor(
    group: string,
    validatorContext?: Partial<ValidationContextBase>
  ) {
    super(
      'unique',
      group,
      (value, context) => {
        if (!value) {
          return of(true);
        }
        // Don't rerun validation if the value hasn't changed from last time
        const model = context.model;
        const statePropertyName = `$unique$${context.propertyName}`;
        if (model[statePropertyName]?.previousValue === value) {
          return of(model[statePropertyName].lastResult);
        }

        const entityTypeName = context.entityTypeName;
        if (!entityTypeName) {
          throw new Error(
            'AsyncUniqueValidator: context.entityTypeName is required'
          );
        }
        const resourceName = context.resourceName;
        if (!resourceName) {
          throw new Error(
            'AsyncUniqueValidator: context.resourceName is required'
          );
        }
        const predicateExpression = context.predicate;
        if (!predicateExpression) {
          throw new Error(
            'AsyncUniqueValidator: context.predicate is required'
          );
        }

        const unitOfWork: UnitOfWorkBase = context.unitOfWork;
        const repository = unitOfWork.createRepository(
          entityTypeName,
          resourceName
        );

        // Evaluate predicate and execute query
        if (!context.predicateFn) {
          const evalFunc = `function(context, model){ return ${parseOptionalChaining(
            predicateExpression
          )} }`;
          context.predicateFn = Function(`"use strict";return (${evalFunc})`)();
        }
        let predicate = context.predicateFn(context, context.model);
        if (Array.isArray(predicate[0])) {
          predicate = Predicate.and(predicate);
        }
        return repository
          .fetch((qb) => qb.where(predicate))
          .pipe(
            map((results) => {
              const validationResult = results.length === 0;

              // Remember result and value
              model[statePropertyName] = {
                previousValue: value,
                lastResult: validationResult,
              };

              return validationResult;
            })
          );
      },
      {
        ...validatorContext,
        messageTemplate:
          validatorContext?.messageTemplate ||
          AsyncUniqueValidator.messageTemplate,
      }
    );
  }

  static create(
    group: string,
    context?: Partial<ValidationContextBase>
  ): AsyncUniqueValidator {
    return new AsyncUniqueValidator(group, context);
  }
}

export const uniqueValidatorProviders: ValueProvider[] = [
  {
    provide: VALIDATOR_FACTORY,
    useValue: {
      name: 'unique',
      fn: SyncUniqueValidator.create,
    },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: {
      name: 'unique',
      fn: AsyncUniqueValidator.create,
    },
    multi: true,
  },
];
